import { useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import type { PdfObjectDiscovery } from "contexts/explorerGlobals/reducer";
import { useHidden } from "contexts/hidden";
import { selectIsProcoreDesign, selectSceneId } from "slices/explorer";
import { SelectionSet } from "utils/selectionSet";

export type ObjectTreeHidden = {
    /** Hidden ids that belong in the object tree (excludes Procore PDF/drawing sheets). */
    objectTreeHiddenCount: number;
    /** Object-tree ids in the hidden toggle snapshot. */
    objectTreeSnapshotCount: number;
    /** Undefined while PDF discovery for the current scene is still outstanding. */
    pdfObjectIds: SelectionSet | undefined;
};

function countObjectTreeIds(ids: Iterable<number>, pdfObjectIds: SelectionSet): number {
    let count = 0;
    for (const id of ids) {
        if (!pdfObjectIds.has(id)) {
            count++;
        }
    }
    return count;
}

/**
 * Split hidden ids into object-tree items (what the reset flyout toggles/clears)
 * and PDF/drawing sheet ids (which stay hidden in 3D and are not part of that category).
 */
export function partitionHiddenIds(
    ids: Iterable<number>,
    pdfObjectIds: SelectionSet,
): { objectTreeIds: number[]; pdfIds: number[] } {
    const objectTreeIds: number[] = [];
    const pdfIds: number[] = [];
    for (const id of ids) {
        (pdfObjectIds.has(id) ? pdfIds : objectTreeIds).push(id);
    }
    return { objectTreeIds, pdfIds };
}

/**
 * The sheet ids to exclude from the object-tree counts, or undefined while
 * discovery for `sceneId` is still outstanding.
 *
 * A failed search resolves to an empty set, so the counts include the hidden
 * sheets. That overcounts, but keeps the flyout category — and with it the only
 * way to unhide from there — available; withholding the ids would drop the
 * category for the rest of the session.
 */
function resolvePdfObjectIds(discovery: PdfObjectDiscovery, sceneId: string | null): SelectionSet | undefined {
    if (discovery.sceneId !== sceneId) {
        return undefined;
    }
    switch (discovery.status) {
        case "ready":
            return discovery.objectIds;
        case "failed":
            return SelectionSet.EMPTY;
        default:
            return undefined;
    }
}

/** PDF/drawing sheet ids only — used when clearing the objects-hidden category. */
export function collectPdfHiddenIds(ids: Iterable<number>, pdfObjectIds: SelectionSet): number[] {
    const pdfIds: number[] = [];
    for (const id of ids) {
        if (pdfObjectIds.has(id)) {
            pdfIds.push(id);
        }
    }
    return pdfIds;
}

/** @returns The PDF/drawing sheet ids, or `undefined` while discovery for the current scene is outstanding. */
export function usePdfObjectIds(): SelectionSet | undefined {
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const sceneId = useAppSelector(selectSceneId);
    const {
        state: { pdfObjectDiscovery },
    } = useExplorerGlobals();

    return isProcoreDesign ? resolvePdfObjectIds(pdfObjectDiscovery, sceneId) : SelectionSet.EMPTY;
}

/**
 * Hidden-object counts for the reset flyout / modification pill.
 *
 * PDFs still render and hide in 3D as usual. This only stops the reset flyout
 * from treating Procore 2D sheets (PDF/drawing documents) as hidden object-tree items.
 */
export function useObjectTreeHidden(): ObjectTreeHidden {
    const hidden = useHidden();
    const pdfObjectIds = usePdfObjectIds();
    const hiddenIdArr = hidden.idArr;

    return useMemo(() => {
        const snapshot = hidden.toggleSnapshot;
        if (pdfObjectIds === SelectionSet.EMPTY) {
            return {
                objectTreeHiddenCount: hiddenIdArr.length,
                objectTreeSnapshotCount: snapshot.size,
                pdfObjectIds,
            };
        }

        if (!pdfObjectIds) {
            return {
                objectTreeHiddenCount: 0,
                objectTreeSnapshotCount: 0,
                pdfObjectIds: undefined,
            };
        }

        return {
            objectTreeHiddenCount: countObjectTreeIds(hiddenIdArr, pdfObjectIds),
            objectTreeSnapshotCount: countObjectTreeIds(snapshot, pdfObjectIds),
            pdfObjectIds,
        };
    }, [hiddenIdArr, hidden.toggleSnapshot, pdfObjectIds]);
}
